import { useNavigate } from '@tanstack/react-router';
import { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useTaxonomyStudioStore } from '~/store/taxonomy-studio.store';
import { copyToClipboard } from '~/utils/clipboard';

import { TaxonomyStudioContext } from '../context/taxonomy-studio-context';
import {
  isAccessProblem,
  isFormProblem,
  isRetryable,
  problemFallbackKey,
  problemTitleKey,
  readProblem,
  type TaxonomyProblem,
} from '../utils/taxonomy-errors';

export interface NotifyOptions {
  /** Re-runs the failed request — offered on network errors, timeouts, 5xx and CONFLICT. */
  retry?: (() => void) | undefined;
  /** Readable names for bulk `items[].key` (e.g. node id → node name). */
  itemNames?: Record<string, string> | undefined;
}

/**
 * The one way Taxonomy Studio reports a failed call. Picks the surface by problem:
 * access problems → full-page message (no toast storm); never-imported → empty state;
 * everything else → one deduplicated toast with a friendly title, the server `detail`,
 * per-item failures, and the right action (Retry, Copy reference, Switch to unpublished
 * changes, Review checks).
 */
export function useTaxonomyErrors() {
  const { t } = useTranslation('taxonomy-studio');
  const navigate = useNavigate();
  // Nullable on purpose: also usable from places rendered outside the studio layout.
  const studio = useContext(TaxonomyStudioContext);

  /** Friendly title + explanation for a problem (never raw JSON). */
  const describe = useCallback(
    (p: TaxonomyProblem) => {
      const title = t(problemTitleKey(p), { defaultValue: t('errors.title.UNKNOWN') });
      const detail = p.detail || t(problemFallbackKey(p));
      return { title, detail };
    },
    [t],
  );

  const notify = useCallback(
    (err: unknown, opts: NotifyOptions = {}) => {
      const p = readProblem(err);
      const store = useTaxonomyStudioStore.getState();
      if (isAccessProblem(p)) {
        store.setAccessProblem({ status: p.status, code: p.code, detail: p.detail });
        return;
      }
      if (p.code === 'NOT_INITIALIZED') {
        store.setNotInitialized(true);
        return;
      }
      const { title, detail } = describe(p);
      const id = `taxonomy-error:${p.code ?? String(p.status ?? p.kind)}:${detail}`;
      const lines: string[] = [detail];
      if (p.items.length) {
        for (const item of p.items.slice(0, 5)) {
          const name = opts.itemNames?.[item.key] ?? item.key;
          lines.push(`• ${name}: ${item.detail || item.code}`);
        }
        if (p.items.length > 5) lines.push(t('errors.moreItems', { count: p.items.length - 5 }));
      }
      if (p.requestId) lines.push(t('errors.reference', { id: p.requestId }));
      const description = (
        <div className="space-y-0.5 whitespace-pre-line">
          {lines.map((l, i) => (
            <p key={i}>{l}</p>
          ))}
        </div>
      );

      let action: { label: string; onClick: () => void } | undefined;
      if (p.code === 'READ_ONLY_RELEASE' && studio) {
        action = {
          label: t('errors.action.switchToDraft'),
          onClick: () => {
            studio.setReleaseRef(undefined);
          },
        };
      } else if (p.code === 'CHECKS_FAILED') {
        store.setChecksOpen(true);
        action = {
          label: t('errors.action.reviewChecks'),
          onClick: () => {
            store.setChecksOpen(true);
          },
        };
      } else if (p.code === 'DRAFT_OPEN') {
        action = {
          label: t('errors.action.review'),
          onClick: () => {
            void navigate({ to: '/taxonomy/versions', search: { seq: 'draft', view: 'release' } });
          },
        };
      } else if (isRetryable(p) && opts.retry) {
        const retry = opts.retry;
        action = { label: t('errors.action.retry'), onClick: retry };
      } else if (p.requestId) {
        const ref = p.requestId;
        action = {
          label: t('errors.action.copyReference'),
          onClick: () => {
            copyToClipboard(ref);
            toast.success(t('errors.copied'));
          },
        };
      }

      toast.error(title, {
        id,
        description,
        duration: isRetryable(p) || p.requestId ? 12_000 : 8_000,
        ...(action ? { action } : {}),
      });
    },
    [describe, navigate, studio, t],
  );

  return { notify, describe };
}

/**
 * Inline error state for a form: field messages keyed by API field name plus one
 * form-level message. `capture` returns true when the problem belongs in the form (422 /
 * 409 validation and business rules), so the caller keeps the dialog open with the
 * user's values; anything else (network, 500, access) falls through to `notify`.
 */
export function useFormErrors() {
  const [fields, setFields] = useState<Record<string, string>>({});
  const [form, setForm] = useState<string | null>(null);

  const clear = useCallback(() => {
    setFields({});
    setForm(null);
  }, []);

  const capture = useCallback((err: unknown): boolean => {
    const p = readProblem(err);
    if (!isFormProblem(p) || p.code === 'STALE_ITEM') return false;
    setFields(p.fieldErrors);
    // A message tied to a field shows under that input; anything else at the form level.
    setForm(Object.keys(p.fieldErrors).length ? null : p.detail || null);
    return true;
  }, []);

  /** Form-level message: the general one, or field messages the form has no input for. */
  const formFor = useCallback(
    (knownFields: string[]): string | null => {
      if (form) return form;
      const orphans = Object.entries(fields)
        .filter(([f]) => !knownFields.includes(f))
        .map(([, m]) => m);
      return orphans.length ? orphans.join(' ') : null;
    },
    [fields, form],
  );

  return { fields, form, formFor, capture, clear };
}
